import { BlockType } from '@next-space/fe-api-idl';
import type { TableOfContentData } from '@tiptap-pro/extension-table-of-contents';
import type { FC } from 'react';
import { createContext, memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { cx } from '../../cx';
import type { PcIconName } from '../../icon-svg/pc-icon';
import { Icon } from '../../icon-svg/pc-icon';
import { Tooltip } from '../../tooltip';
import { getScrollChatList } from '../const';
import { useChatContext, useConversationContext } from '../hooks/context';
import { getRefData, useChat } from '../hooks/hook';
import { useChatTranslation } from '../hooks/use-i18n';
import { formatReferenceTextToMd } from '../hooks/utils';
import { MindMap, useOpenMindMap } from './mind-map';

enum MenuType {
  MENU_LIST = 'menu_list',
  MIND_MAP = 'mind_map',
}

interface MenuContextType {
  activeMenu: MenuType;
  setActiveMenu: (type: MenuType) => void;
}

const MenuContext = createContext<MenuContextType>({
  activeMenu: MenuType.MENU_LIST,
  setActiveMenu: () => {},
});

export const SideBar: FC = memo(() => {
  const { ai, user, resultData } = useChat();
  const [activeMenu, setActiveMenu] = useState<MenuType>(MenuType.MENU_LIST);
  const openMindMap = useOpenMindMap();
  const { onSaveArticle } = useConversationContext();
  const { t } = useChatTranslation();
  const refData = useMemo(() => getRefData(resultData), [resultData]);

  const savePage = useCallback(() => {
    onSaveArticle({
      ai: formatReferenceTextToMd(ai, refData),
      saveType: BlockType.MIND_MAPPING_PAGE,
      user,
    });
  }, [ai, onSaveArticle, refData, user]);

  return (
    <MenuContext.Provider value={{ activeMenu, setActiveMenu }}>
      <div className="border rounded text-t2 text-normal">
        <HeaderBar />
        {activeMenu === MenuType.MENU_LIST ? (
          <HeadingList />
        ) : (
          <div className="relative group">
            <MindMap ai={ai} className="bg-white1 h-52" />
            <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity space-x-6 flex items-center">
              <Tooltip popup={t('chatMindMapPreview')}>
                <Icon name="IcFullScreen" size="middle" onClick={() => openMindMap({ ai })} />
              </Tooltip>
              <Tooltip popup={t('chatMindMapSave')}>
                <Icon name="IcEdit" size="middle" onClick={savePage} />
              </Tooltip>
            </div>
          </div>
        )}
      </div>
    </MenuContext.Provider>
  );
});

const MENU_ITEMS = [
  {
    type: MenuType.MENU_LIST,
    labelKey: 'chatSidebarTableOfContents',
    icon: 'IcList',
  },
  {
    type: MenuType.MIND_MAP,
    labelKey: 'chatSidebarMindMap',
    icon: 'IcMindmap',
  },
];

const HeaderBar = memo(() => {
  const { activeMenu, setActiveMenu } = useContext(MenuContext);
  const { t } = useChatTranslation();

  return (
    <div className="py-2 px-4 border-b flex gap-6">
      {MENU_ITEMS.map((item) => (
        <span
          key={item.type}
          className={cx('animate-click space-x-1 flex items-center text-t2', {
            'text-grey3': activeMenu !== item.type,
          })}
          onClick={() => setActiveMenu(item.type)}
        >
          <Icon name={item.icon as PcIconName} size="middle" />
          <span>{t(item.labelKey)}</span>
        </span>
      ))}
    </div>
  );
});

const HeadingList = memo(() => {
  const { toc = [] } = useChatContext();
  const [activeId, setActiveId] = useState<string>(() => toc[0]?.id || '');
  const { conversationId } = useConversationContext();
  const { t } = useChatTranslation();

  useEffect(() => {
    const firstId = toc[0]?.id;
    if (firstId && !activeId) {
      setActiveId(firstId);
    }
  }, [toc, activeId]);

  const initObserver = useCallback(() => {
    const container = document.getElementById(getScrollChatList(conversationId));
    if (!container || !toc.length) return null;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter((entry) => entry.isIntersecting);
        if (visibleEntries.length === 0) return;

        const mostVisible = visibleEntries.reduce((prev, current) => {
          return current.intersectionRatio > prev.intersectionRatio ? current : prev;
        });

        if (mostVisible.target.id) {
          setActiveId(mostVisible.target.id);
        }
      },
      {
        root: container,
        threshold: 0.5,
        rootMargin: '50px 0px -80% 0px',
      }
    );

    let hasValidElements = false;
    for (const item of toc) {
      const element = document.getElementById(item.id);
      if (element) {
        observer.observe(element);
        hasValidElements = true;
      }
    }

    return hasValidElements ? observer : null;
  }, [conversationId, toc]);

  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    let retryCount = 0;
    const maxRetries = 3;

    const tryInit = () => {
      observer = initObserver();
      if (!observer && retryCount < maxRetries) {
        retryCount++;
        setTimeout(tryInit, 500);
      }
    };

    // 确保 DOM 已加载
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', tryInit);
    } else {
      tryInit();
    }

    return () => {
      observer?.disconnect();
      document.removeEventListener('DOMContentLoaded', tryInit);
    };
  }, [initObserver]);

  if (!toc.length) {
    return <div className="p-4 text-grey3">{t('chatSidebarNoContents')}</div>;
  }

  return (
    <div className="p-2 h-full overflow-auto max-h-[calc(65vh)]">
      {toc.map((item) => (
        <MenuItem key={item.id} item={item} isActive={activeId === item.id} />
      ))}
    </div>
  );
});

interface MenuItemProps {
  item: TableOfContentData[number];
  isActive: boolean;
}

const MenuItem = memo<MenuItemProps>((props) => {
  const { item, isActive } = props;
  const { conversationId } = useConversationContext();

  const handleClick = useCallback(() => {
    const container = document.getElementById(getScrollChatList(conversationId));
    const element = document.getElementById(item.id);
    if (container && element) {
      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const relativeTop = elementRect.top - containerRect.top + container.scrollTop;
      container.scrollTo({ top: relativeTop });
    }
  }, [conversationId, item.id]);

  return (
    <div
      className={cx('text-black px-2 py-1 rounded animate-hover animate-click truncate', {
        'bg-black_006': isActive,
      })}
      style={{ paddingLeft: `${item.level * 8}px` }}
      onClick={handleClick}
    >
      {item.textContent}
    </div>
  );
});
